import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { Dictionary } from '@ngrx/entity';
import { Store } from '@ngrx/store';
import { format, isPast } from 'date-fns';
import dateFormat from 'date-fns/format';
import isSameDay from 'date-fns/is_same_day';
import { get as _get, sortBy as _sortBy } from 'lodash';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { filter, map, startWith, take, takeUntil } from 'rxjs/operators';

import { BillAccountsSelectors } from '@amfam/billing/billaccount/data-access';
import { OneTimePayActions } from '@amfam/billing/one-time-pay/feature';
import {
  PaymentMethodActions,
  PaymentMethodSelectors
} from '@amfam/billing/payment-method/data-access';
import {
  PaymentMethod,
  PaymentMethodAccountType,
  PaymentMethodAddEditConfig,
  PaymentMethodAddEditOperationType,
  PaymentMethodUtilService
} from '@amfam/billing/payment-method/util';
import { pendingPaymentActions } from '@amfam/billing/pending-payment/data-access';
import {
  PaymentTermsAndConditionsPartnerContent,
  PaymentTermsAndConditionsType
} from '@amfam/billing/shared/ui';
import { BillAccount, MatDatePickerOptions } from '@amfam/shared/models';
import { CurrencyUSDPipe } from '@amfam/shared/ui/pipes';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { ImpersonateRolesService } from '@amfam/shared/utility/impersonation';
import { routerQuery } from '@amfam/shared/utility/navigation';
import {
  Applications,
  ApplicationService,
  CopyService,
  UtilService
} from '@amfam/shared/utility/shared-services';
import {
  AlertModalConfig,
  DockingBarService,
  DsModalService,
  LoadingSpinnerService,
  Option
} from '@amfam/ui-kit';

import { OneTimePayFeatureFacade } from '../../+state/one-time-pay-feature.facade';
import {
  ChoosePaymentAmountModal,
  OneTimePaySetup,
  OTPReviewData,
  PaymentMethodRelatedPaymentErrorCodes,
  TotalAmount
} from '../../+state/one-time-pay-feature.models';

@Component({
  selector: 'amfam-review-payment-wrapper',
  templateUrl: './review-payment-wrapper.component.html',
  styleUrls: ['./review-payment-wrapper.component.scss'],
  standalone: false
})
export class ReviewPaymentWrapperComponent implements OnInit, OnDestroy, AfterViewInit {
  paymentDateOptions: Option[];
  defaultPaymentDate: string;
  matDatePickerOptions: MatDatePickerOptions;
  cancelModalConfig: AlertModalConfig = {
    headerText: 'Are you sure?',
    bodyText: 'If you leave now some of your changes won’t be saved.',
    modalId: 'oneTimePayCancelModal',
    ctaConfig: {
      primaryButtonName: "I'm sure",
      primaryButtonDestructive: true,
      tertiaryButtonName: 'Cancel',
      fullWidthButtons: true
    }
  };
  hasPastDue$: Observable<boolean>;
  deleteAccountModalConfig: AlertModalConfig = {
    headerText: 'Are you sure?',
    bodyText: 'Removing this account means you’ll have to pay it separately.',
    modalId: 'deleteAccountModal',
    ctaConfig: {
      primaryButtonName: 'Remove Account',
      primaryButtonDestructive: true,
      tertiaryButtonName: 'Cancel',
      fullWidthButtons: true
    }
  };
  selectedAccountId: string;
  authorizationDisclaimer: string;
  displayPaymentExpiredError$!: Observable<boolean>;
  oneTimePayForm: UntypedFormGroup;
  accountListOptions$: Observable<OneTimePaySetup[]>;
  autoPaySetUpLinkText: string;
  showAutoPaySetUpLink$: Observable<boolean> = this.oneTimePayFacade.showAutoPaySetUpLink();
  paymentMethodOptions: Option[];
  defaultPaymentMethod: Option;
  paymentMethodAddEditConfig$: BehaviorSubject<PaymentMethodAddEditConfig> = new BehaviorSubject(
    null
  );
  authorizedToAddPaymentMethod$: Observable<boolean>;
  authorizedToEditPaymentMethod$: Observable<boolean>;
  paymentMethodError: boolean;
  total$: Observable<TotalAmount>;
  dueDate: string;
  choosePaymentAmountOptions: ChoosePaymentAmountModal;
  choosePaymentAmountConfig$: BehaviorSubject<string> = new BehaviorSubject(null);
  defaultPaymentAmount: number;
  headingText: string;
  bodyText: string;
  linkText: string;
  postLinkText: string;
  linkEventText: string;
  alertLinkEventBillAccountNumber: string;
  errorBoxText: boolean;
  displayDate$: Observable<string> = of('Today');
  primaryButtonName = 'Make a Payment';
  alertsTitleList: string[] = [];
  isFormInvalid$: Observable<boolean>;
  isEditOTPFlow$ = this.oneTimePayFacade.isEditOTP();
  hasMinDueZero$: Observable<boolean>;
  isMinDueZero: boolean;
  isMyAccountAdmin = this.appService.isApp(Applications.MYACCOUNT_ADMIN);
  showTermsAndConditionsModal = false;
  isEditingAndNotChanged$: Observable<boolean>;
  isEditingAndDateNotChanged = true;
  isEditingAndAmountNotChanged: boolean;
  defaultOTPEntities: Dictionary<OneTimePaySetup>;
  isDueDateInPast: boolean;
  termsAndConditionsType$: Observable<PaymentTermsAndConditionsType>;
  isPartner$: Observable<boolean>;
  termsAndConditionsPartnerContent$: Observable<PaymentTermsAndConditionsPartnerContent>;
  paymentError$: Observable<string>;
  isDisabledAfterClick: boolean;

  private stop$ = new Subject<void>();

  constructor(
    private store: Store,
    private formBuilder: UntypedFormBuilder,
    private modalService: DsModalService,
    private paymentMethodUtil: PaymentMethodUtilService,
    public roleService: ImpersonateRolesService,
    private utilService: UtilService,
    private spinner: LoadingSpinnerService,
    private dockingBar: DockingBarService,
    private copyService: CopyService,
    private oneTimePayFacade: OneTimePayFeatureFacade,
    private appService: ApplicationService,
    private feature: FeatureFlagService
  ) {}

  ngOnInit(): void {
    this.spinner.stop();
    this.isDisabledAfterClick = false;
    this.store
      .select(routerQuery.getRouterPreviousStateUrl)
      .pipe(take(1))
      .subscribe(url => {
        if (!url.includes('choose-accounts'))
          this.store.dispatch(OneTimePayActions.setOneTimePayCancelPath({ url }));
      });
    this.dockingBar.registerHeading(
      this.copyService.getCopy('billing.oneTimePayRefactor.dockingBarHeading')
    );
    this.accountListOptions$ = this.oneTimePayFacade.getBillAccountsWithAlertMessageList();
    this.autoPaySetUpLinkText = this.isMyAccountAdmin
      ? this.copyService.getCopy(
          'billing.oneTimePayRefactor.myAccountAdmin.setUpAutoPaySubHeaderAdmin'
        )
      : (this.autoPaySetUpLinkText = this.copyService.getCopy(
          'billing.oneTimePayRefactor.setUpAutoPaySubHeader'
        ));
    this.getAlertMessage();
    this.total$ = this.calculateTotalAmount();
    this.hasPastDue$ = this.hasPastDueAccounts();
    this.hasMinDueZero$ = this.minDueIsZero();
    this.getForm();
    this.calenderOptions();
    this.authorizedToAddPaymentMethod$ = this.roleService.isAuthorized(
      'makepaymentacct_walkthrough'
    );
    this.authorizedToEditPaymentMethod$ = this.roleService.isAuthorized(
      'editpaymentacct_walkthrough'
    );
    this.displayPaymentExpiredError$ = this.getExpiredPayment();
    this.isFormInvalid$ = this.isFormInvalid();
    this.isEditingAndNotChanged$ = this.getIsEditingAndNotChanged();
    this.comparePaymentAmountsEditOTP();
    this.termsAndConditionsType$ = this.getPaymentTermsAndConditionsType();
    this.isPartner$ = this.store.select(BrandSelectors.selectIsPartner);
    this.termsAndConditionsPartnerContent$ = this.getPaymentTermsAndConditionsPartnerContent();
    this.paymentError$ = this.getPaymentError();
  }

  getPaymentTermsAndConditionsType(): Observable<PaymentTermsAndConditionsType> {
    return combineLatest([
      this.oneTimePayForm
        .get('paymentMethod')
        .valueChanges.pipe(startWith(this.oneTimePayForm.get('paymentMethod').value)),
      this.store.select(PaymentMethodSelectors.selectPaymentMethods)
    ]).pipe(
      filter(([value]) => value),
      map(([value, paymentMethods]) => {
        const paymentMethod: PaymentMethod = paymentMethods.find(
          payment => payment.paymentAccountId === value.id
        );
        const paymentMethodType: PaymentMethodAccountType =
          this.paymentMethodUtil.getPaymentMethodAccountType(paymentMethod);
        return paymentMethodType === PaymentMethodAccountType.CREDIT_DEBIT
          ? PaymentTermsAndConditionsType.SCHEDULED_PAYMENT_CARD
          : PaymentTermsAndConditionsType.SCHEDULED_PAYMENT_BANK;
      })
    );
  }

  getPaymentTermsAndConditionsPartnerContent() {
    return this.store.select(BrandSelectors.selectPaymentTermsAndConditionsPartnerContent);
  }

  isFormInvalid(): Observable<boolean> {
    return combineLatest([
      this.oneTimePayForm
        .get('paymentDate')
        .valueChanges.pipe(startWith(this.oneTimePayForm.get('paymentDate').value)),
      this.oneTimePayForm
        .get('paymentDatePicker')
        .valueChanges.pipe(startWith(this.oneTimePayForm.get('paymentDatePicker').value))
    ]).pipe(
      map(([paymentDate, paymentDatePicker]) => {
        if (paymentDate === '' && (paymentDatePicker === '' || paymentDatePicker === null)) {
          return true;
        } else {
          return false;
        }
      })
    );
  }

  ngAfterViewInit(): void {
    combineLatest([this.oneTimePayFacade.isEditOTP(), this.hasPastDue$])
      .pipe(take(1))
      .subscribe(([isEditOTP, hasPastDue]) => {
        if (isEditOTP) {
          this.oneTimePayFacade.sendAnalytics({
            pageAnalyticName: 'Edit:Start'
          });
        } else {
          if (this.feature.isEnabled('simplified_payment')) {
            this.oneTimePayFacade.sendSimplifiedPaymentAnalytics();
          } else {
            // AS: Check when we have to pass 'Payment Already Scheduled'/'Account Enrolled AutoPay'
            this.oneTimePayFacade.sendAnalytics({
              pageAnalyticName: 'Start',
              pageAnalyticSubCategory3: this.getSubCategory3ForAnalytics(
                this.alertsTitleList,
                hasPastDue
              )
            });
          }
        }
      });
  }

  getSubCategory3ForAnalytics(alertMessageTitleList: string[], isPastDue: boolean) {
    return isPastDue
      ? 'Past Due'
      : this.isScheduledPaymentAlert(alertMessageTitleList)
        ? 'Payment Already Scheduled'
        : this.isAutoPaySetupAlert(alertMessageTitleList)
          ? 'Account Enrolled AutoPay'
          : '';
  }

  isAutoPaySetupAlert(alertMessageTitleList: string[]): boolean {
    return !!alertMessageTitleList.find(title => title === 'hasAutoPay');
  }

  isScheduledPaymentAlert(alertMessageTitleList: string[]): boolean {
    return (
      !!alertMessageTitleList.find(title => title === 'hasScheduledPayment') ||
      !!alertMessageTitleList.find(title => title === 'hasMultipleScheduledPayments') ||
      !!alertMessageTitleList.find(title => title === 'hasImmediateScheduledPayment')
    );
  }

  getForm(): void {
    combineLatest([
      this.getPaymentDateOptions(),
      this.oneTimePayFacade.getPaymentMethodOptions(),
      this.oneTimePayFacade.getOTPEntities(),
      this.oneTimePayFacade.getBillAccountsList()
    ])
      .pipe(take(1))
      .subscribe(
        ([paymentDateOptions, paymentMethodOptions, otpEntities, selectedBillaccounts]) => {
          this.paymentMethodOptions = paymentMethodOptions;
          this.paymentDateOptions = paymentDateOptions;
          const key = Object.keys(otpEntities)[0];
          this.defaultOTPEntities = otpEntities;
          this.defaultPaymentDate = otpEntities[key].selectedPaymentDate;
          this.oneTimePayForm = this.formBuilder.group({
            paymentMethod: [
              this.getDefaultPaymentMethod(
                paymentMethodOptions,
                otpEntities[key].selectedPaymentAccount
              ),
              Validators.compose([Validators.required])
            ],
            paymentDate: [
              this.getDefaultDate(paymentDateOptions, otpEntities[key].selectedPaymentDate),
              Validators.compose([Validators.required])
            ],
            paymentDatePicker: [
              this.getDefaultDatePickerValue(
                paymentDateOptions,
                otpEntities[key].selectedPaymentDate
              ),
              Validators.compose([])
            ]
          });
          this.oneTimePayForm = this.buildControls(
            this.oneTimePayForm,
            selectedBillaccounts,
            otpEntities
          );
        }
      );
  }

  getDefaultPaymentMethod(paymentMethodOptions: Option[], paymentNickName: string) {
    this.defaultPaymentMethod = paymentMethodOptions.find(
      paymentMethodOption => paymentMethodOption.text === paymentNickName
    );
    return this.defaultPaymentMethod ? this.defaultPaymentMethod : paymentMethodOptions[0];
  }

  getDefaultDate(paymentDateOptions: Option[], paymentDate: string) {
    if (isSameDay(new Date(), paymentDate)) {
      return paymentDateOptions[0];
    } else if (isSameDay(this.dueDate, paymentDate)) {
      return paymentDateOptions[1];
    } else if (paymentDate) {
      return '';
    } else {
      return paymentDateOptions[0];
    }
  }

  getDefaultDatePickerValue(paymentDateOptions: Option[], paymentDate: string) {
    if (paymentDate && this.getDefaultDate(paymentDateOptions, paymentDate) === '') {
      const d: Date = new Date(dateFormat(paymentDate, 'MM/DD/YYYY'));
      const paymentDateObject = {
        date: { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() },
        jsdate: d.toISOString(),
        formatted: dateFormat(d, 'MMM DD YYYY')
      };
      return paymentDateObject;
    }
    return '';
  }

  getExpiredPayment(): Observable<boolean> {
    return combineLatest([
      this.oneTimePayForm.get('paymentMethod').valueChanges,
      this.store.select(PaymentMethodSelectors.selectPaymentMethods)
    ]).pipe(
      map(([changes, paymentMethods]) => {
        const paymentMethod: PaymentMethod = paymentMethods.find(
          payment => payment.paymentAccountId === (changes as Option)?.id
        );

        return this.paymentMethodUtil.isPaymentMethodExpired(paymentMethod);
      })
    );
  }

  getPaymentDateOptions(): Observable<Option[]> {
    return this.oneTimePayFacade.getBillAccountsList().pipe(
      map(accounts => {
        const todayFormattedDate: string = format(new Date(), 'MMM DD');
        const earliestDate = _sortBy(accounts, 'dueDate') || [];
        this.dueDate = earliestDate[0]?.dueDate;
        const dueDateFormatted = format(this.dueDate, 'MMM DD');
        // If due date is today, handle it same way as due date in past
        this.isDueDateInPast = isPast(this.dueDate);
        const paymentOptions = [
          { text: 'Today', id: 'TODAY', helpText: [{ text: todayFormattedDate }] }
        ];
        if (
          this.dueDate !== '0001-01-01' && // means that there isn't a due date establihsed yet
          this.dueDate !== '0000-12-31' && // means that there isn't a due date establihsed yet
          !isPast(this.dueDate) // due date can't be in the past
        ) {
          paymentOptions.push({
            text: 'Due Date',
            id: 'DUEDATE',
            helpText: [{ text: `${dueDateFormatted}` }]
          });
        }
        return paymentOptions;
      })
    );
  }

  calenderOptions() {
    combineLatest([this.hasMinDueZero$, this.hasPastDue$])
      .pipe(takeUntil(this.stop$))
      .subscribe(([hasMinDueZero, hasPastDue]) => {
        this.isMinDueZero = hasMinDueZero;
        this.matDatePickerOptions = {
          isDisabled:
            this.isDueDateInPast || hasPastDue || hasMinDueZero || this.dueDate === '0001-01-01', // when the date is 0001-01-01 that means that there isn't a due date establihsed yet
          minDate: new Date(),
          maxDate: new Date(this.dueDate.replace(/-/g, '/')),
          highlightDate: new Date(this.dueDate.replace(/-/g, '/'))
        };
      });
  }

  calculateTotalAmount(): Observable<TotalAmount> {
    return this.oneTimePayFacade.getBillAccountsList().pipe(
      takeUntil(this.stop$),
      map(accounts => {
        let sum = 0;
        accounts.map(
          account =>
            (sum +=
              Math.round(
                Number(
                  _get(account, 'selectedPaymentAmount', account.minDue) as unknown as number
                ) * 100
              ) / 100)
        );
        return {
          dollars: Math.floor(sum).toString(),
          cents: sum.toFixed(2).toString().split('.')[1]
        };
      })
    );
  }

  dateChangeEvent(selectedDate: string) {
    let selectedPaymentDateFormatted;
    if (this.oneTimePayForm.controls['paymentDate'].value.id === 'TODAY') {
      this.primaryButtonName = 'Make a Payment';
      selectedPaymentDateFormatted = 'Today';
    } else if (this.oneTimePayForm.controls['paymentDate'].value.id === 'DUEDATE') {
      this.primaryButtonName = 'Schedule Payment';
      selectedPaymentDateFormatted = format(this.dueDate, 'MMM DD, YYYY');
    } else if (format(new Date(), 'MMM DD, YYYY') === selectedDate) {
      this.primaryButtonName = 'Make a Payment';
      selectedPaymentDateFormatted = 'Today';
    } else {
      this.primaryButtonName = 'Schedule Payment';
      selectedPaymentDateFormatted = selectedDate;
    }
    this.displayDate$ = of(selectedPaymentDateFormatted);
    const defaultDateObj = this.getDefaultDate(this.paymentDateOptions, this.defaultPaymentDate);
    const defaultDatePickerVal = format(this.defaultPaymentDate, 'MMM DD, YYYY');
    if (
      (defaultDateObj &&
        this.oneTimePayForm.controls['paymentDate'].value.id &&
        defaultDateObj.id !== this.oneTimePayForm.controls['paymentDate'].value.id) ||
      (selectedDate && defaultDatePickerVal !== selectedDate)
    ) {
      this.isEditingAndDateNotChanged = false;
    } else {
      this.isEditingAndDateNotChanged = true;
    }
  }

  hasPastDueAccounts(): Observable<boolean> {
    return this.oneTimePayFacade.getBillAccountsList().pipe(
      takeUntil(this.stop$),
      map(accounts => accounts.some(account => account.pastDue === true))
    );
  }

  minDueIsZero(): Observable<boolean> {
    return this.oneTimePayFacade.getBillAccountsList().pipe(
      takeUntil(this.stop$),
      map((billAccounts: OneTimePaySetup[]) =>
        billAccounts.some(billAccount => billAccount.selectedPaymentAmount.toString() === '0')
      )
    );
  }

  editPaymentMethod(): void {
    const paymentAccountId: string = this.oneTimePayForm.get('paymentMethod').value.id;
    this.store
      .select(PaymentMethodSelectors.selectPaymentMethod(paymentAccountId))
      .pipe(take(1))
      .subscribe((paymentMethod: PaymentMethod) => {
        this.openPaymentMethodModal({
          accountType: this.paymentMethodUtil.getPaymentMethodAccountType(paymentMethod),
          operation: PaymentMethodAddEditOperationType.EDIT,
          paymentMethod
        });
      });
  }

  addCreditDebit(): void {
    this.openPaymentMethodModal({
      accountType: PaymentMethodAccountType.CREDIT_DEBIT,
      operation: PaymentMethodAddEditOperationType.ADD,
      paymentMethod: null
    });
  }

  addCheckingAccount(): void {
    this.openPaymentMethodModal({
      accountType: PaymentMethodAccountType.CHECKING,
      operation: PaymentMethodAddEditOperationType.ADD,
      paymentMethod: null
    });
  }

  addSavingsAccount(): void {
    this.openPaymentMethodModal({
      accountType: PaymentMethodAccountType.SAVINGS,
      operation: PaymentMethodAddEditOperationType.ADD,
      paymentMethod: null
    });
  }

  paymentMethodAddEditComplete(newPaymentAccountId: string): void {
    this.modalService.close('paymentMethodModal');

    const correlationId: string = this.utilService.generateId();
    this.spinner.start({
      blockActions: true
    });
    this.oneTimePayFacade.sendAnalytics({ pageAnalyticName: 'Spinner' });
    this.store.dispatch(PaymentMethodActions.getPaymentMethods({ correlationId }));

    combineLatest([
      this.store.select(PaymentMethodSelectors.selectPaymentMethodState),
      this.oneTimePayFacade.getPaymentMethodOptions()
    ])
      .pipe(
        map(([paymentMethodState, paymentMethodOptions]) => ({
          paymentMethodState,
          paymentMethodOptions
        })),
        filter(state => state.paymentMethodState.correlationId === correlationId),
        takeUntil(this.stop$)
      )
      .subscribe(state => {
        this.spinner.stop();
        if (!state.paymentMethodState.hasError) {
          const newPaymentMethodOption: Option = state.paymentMethodOptions.find(
            (option: Option) => option.id === newPaymentAccountId
          );
          this.paymentMethodOptions = state.paymentMethodOptions;

          this.oneTimePayForm.get('paymentMethod').setValue(newPaymentMethodOption);
          this.paymentMethodError = false;
          this.oneTimePayForm.updateValueAndValidity();
        }
      });
  }

  editAmount(id: string) {
    let selectedAccount: OneTimePaySetup;

    this.oneTimePayFacade.sendAnalytics({
      buttonAnalyticName: 'EditAmount'
    });

    combineLatest([
      this.store.select(BillAccountsSelectors.selectBillAccount(id)),
      this.oneTimePayFacade.getBillAccountsList(),
      this.oneTimePayFacade.getDefaultPayment(id)
    ])
      .pipe(take(1))
      .subscribe(([account, accountListOptions, defaultPaymentAmount]) => {
        selectedAccount = accountListOptions.find(option => id === option?.billAccountNumber);
        this.defaultPaymentAmount = this.getDefaultPaymentAmount(account, defaultPaymentAmount);
        this.choosePaymentAmountOptions = {
          radioButtonOptions: [
            {
              text: new CurrencyUSDPipe().transform(account.minimumAmountDue),
              id: 'minDue',
              helpText: [{ text: this.getMinimumDueText(account) }]
            },
            {
              text: new CurrencyUSDPipe().transform(this.getFullPayValue(account)),
              id: 'accountBalance',
              helpText: [{ text: this.getFullPayText(account) }]
            }
          ],
          accountDescriptionDetails: {
            billAccountNumber: selectedAccount.billAccountNumber,
            riskDescription: selectedAccount.riskDescription,
            policyTypeIcon: selectedAccount.policyTypeIcon,
            accountType: account.accountType,
            isCancellationLetterSent: account.cancelLetterSent
          }
        };
        if (account.minimumAmountDue.toString() === '0') {
          this.choosePaymentAmountOptions.radioButtonOptions.splice(0, 1);
        }
        this.openChoosePaymentAmountModal(id);
      });
  }

  getMinimumDueText(billaccount: BillAccount) {
    if (!billaccount.isAdvance && billaccount.cancelLetterSent) {
      return 'Past Due + Current';
    }
    if (billaccount.pastDue) {
      return 'Past Due';
    }
    return 'Minimum Due';
  }

  getMinimumDueValue(billaccount: BillAccount) {
    return billaccount?.minimumAmountDue;
  }

  getFullPayText(billaccount: BillAccount) {
    if (billaccount.isAdvance && billaccount?.fullPayBalance > 0) {
      return 'Full Pay Amount';
    }
    return 'Account Balance';
  }

  getFullPayValue(billaccount: BillAccount) {
    if (billaccount.isAdvance && Number(billaccount?.fullPayBalance) > 0) {
      return billaccount?.fullPayBalance;
    }
    return billaccount?.accountBalance;
  }

  getDefaultPaymentAmount(_billaccount: BillAccount, defaultAmount: number) {
    if (defaultAmount) {
      return defaultAmount;
    }
    /**
     * AS: This is commented for now since Business can't make up their mind on what
     * to display as default on the full pay available. Based on decision remove this or uncomment.
     */
    // else if (billaccount.isAdvance && Number(billaccount?.fullPayBalance) > 0) {
    //   return Number(billaccount.fullPayBalance);
    // }
    else {
      return null;
    }
  }

  delete(id: string) {
    this.oneTimePayFacade.sendAnalytics({ pageAnalyticName: 'RemoveAccount' });
    this.modalService.open('deleteAccountModal');
    this.selectedAccountId = id;
  }

  setupAutopay() {
    this.oneTimePayFacade.sendAnalytics({ buttonAnalyticName: 'Start:SetupAutoPay' });
    this.modalService.open('oneTimePayCancelModal');
    this.linkEventText = this.copyService.getCopy(
      `billing.oneTimePayRefactor.alertMessages.setupAutopayText`
    );
  }

  saveCustomAmountComplete(selectedPaymentAmount: { billAccountNumber: string; amount: string }) {
    this.modalService.close('choosePaymentAmountModal');
    let amount;
    if (selectedPaymentAmount.amount.indexOf('$') !== -1) {
      amount = parseFloat(selectedPaymentAmount.amount.replace(/\$|,/g, ''));
    } else {
      amount = selectedPaymentAmount.amount;
    }
    this.store.dispatch(
      OneTimePayActions.updatePaymentAmount({
        billAccountNumber: selectedPaymentAmount.billAccountNumber,
        selectedPaymentAmount: Number(amount)
      })
    );

    this.oneTimePayForm.get(selectedPaymentAmount.billAccountNumber).setValue(Number(amount));
    this.accountListOptions$ = this.oneTimePayFacade.getBillAccountsWithAlertMessageList();
    this.getAlertMessage();
  }

  comparePaymentAmountsEditOTP() {
    this.oneTimePayFacade
      .getOTPEntities()
      .pipe(takeUntil(this.stop$))
      .subscribe(entities => {
        this.isEditingAndAmountNotChanged = true;

        Object.values(entities).forEach(entity => {
          if (
            this.isEditingAndAmountNotChanged &&
            this.defaultOTPEntities[entity.billAccountNumber] &&
            entity.selectedPaymentAmount !==
              this.defaultOTPEntities[entity.billAccountNumber].selectedPaymentAmount
          ) {
            this.isEditingAndAmountNotChanged = false;
          }
        });
      });
  }
  getIsEditingAndNotChanged(): Observable<boolean> {
    return combineLatest([
      this.oneTimePayForm.valueChanges.pipe(startWith(this.oneTimePayForm.value)),
      this.isEditOTPFlow$
    ]).pipe(
      map(
        ([, isEditOTP]) =>
          isEditOTP &&
          this.oneTimePayForm?.get('paymentMethod').value.id === this.defaultPaymentMethod.id
      )
    );
  }

  submitPayment(): void {
    if (this.paymentMethodOptions.length === 0) {
      this.paymentMethodError = true;
    } else if (this.isMinDueZero) {
      // do nothing until payment amount is entered
    } else {
      if (this.isMyAccountAdmin) {
        this.showTermsAndConditionsModal = true;
      } else {
        this.submit();
        this.isDisabledAfterClick = true;
      }
    }
  }

  agreeTermsAndConditionsEvent(agree: boolean): void {
    if (agree) {
      this.submit();
    }
  }

  submit(): void {
    this.spinner.start({
      blockActions: true
    });
    this.oneTimePayFacade.sendAnalytics({ pageAnalyticName: 'Spinner' });
    const otpReviewData = this.preprocessPaymentPayload(this.oneTimePayForm, this.dueDate);
    this.oneTimePayFacade.addOTPReviewInfo(otpReviewData);
    this.dispatchPaymentAmountAndDateForAnalytics(otpReviewData, this.dueDate);
    this.oneTimePayFacade
      .isEditOTP()
      .pipe(take(1))
      .subscribe(isEditOTP => {
        if (isEditOTP) {
          this.oneTimePayFacade.editOTP();
        } else {
          this.oneTimePayFacade.submitOTP();
        }
      });
  }

  dispatchPaymentAmountAndDateForAnalytics(otpReviewData: OTPReviewData[], dueDate: string) {
    this.oneTimePayFacade
      .getBillAccountEntities()
      .pipe(take(1))
      .subscribe(billAccountEntities => {
        const selectedPaymentAmount = otpReviewData[0].selectedPaymentAmount;
        const selectedPaymentDate = otpReviewData[0].selectedPaymentDate;
        const billAccountObj = billAccountEntities[otpReviewData[0].billAccountNumber];
        let analyticsAmtText = '';
        let analyticsDateText = '';

        switch (Number(selectedPaymentAmount)) {
          case Number(billAccountObj?.minimumAmountDue):
            analyticsAmtText = 'Minimum Due';
            break;
          case Number(billAccountObj?.accountBalance):
            analyticsAmtText = 'Account Balance';
            break;
          case Number(billAccountObj?.fullPayBalance):
            analyticsAmtText = 'Full Pay';
            break;
          case Number(billAccountObj?.minimumAmountDueBreakdown?.previousAmountDue):
            analyticsAmtText = 'Past Due';
            break;
          case Number(billAccountObj?.minimumAmountDue) +
            Number(billAccountObj?.minimumAmountDueBreakdown?.currentAmountDue):
            analyticsAmtText = 'Past Due + Current';
            break;
          default:
            analyticsAmtText = 'Other Amount';
        }
        this.oneTimePayFacade.addOTPReviewPaymentAmtAnalyticsInfo(analyticsAmtText);

        if (isSameDay(dateFormat(new Date(), 'YYYY-MM-DD'), selectedPaymentDate)) {
          analyticsDateText = 'Today';
        } else if (isSameDay(dateFormat(dueDate, 'YYYY-MM-DD'), selectedPaymentDate)) {
          analyticsDateText = 'Due Date';
        } else {
          analyticsDateText = 'Custom';
        }
        this.oneTimePayFacade.addOTPReviewPaymentDateAnalyticsInfo(analyticsDateText);
      });
  }

  getAlertMessage() {
    this.oneTimePayFacade
      .getBillAccountsWithAlertMessageList()
      .pipe(take(1))
      .subscribe(accounts => {
        // this will reset warningbox/errorbox in case of alert message change
        this.headingText = undefined;
        if (accounts.length === 1) {
          this.headingText = accounts[0].alertMessage?.heading;
          this.bodyText = accounts[0].alertMessage?.body1;
          this.linkText = accounts[0].alertMessage?.body2Text;
          this.postLinkText = accounts[0].alertMessage?.body3;
          this.alertLinkEventBillAccountNumber = accounts[0].billAccountNumber;
          this.errorBoxText = accounts[0].alertMessage?.error;
          this.alertsTitleList.push(accounts[0].alertMessage?.title);
        } else {
          const earliestDate = _sortBy(accounts, 'dueDate') || [];
          const alertTitles = [];
          for (const n in accounts) {
            if (accounts[n].alertMessage) {
              alertTitles.push(accounts[n].alertMessage?.title);
              this.alertsTitleList.push(accounts[n].alertMessage?.title);
            }
          }
          if (alertTitles.length > 0) {
            const isAllPastDue = alertTitles.every(
              title => title === 'hasPastDue' || title === 'hasPastDueLetterSent'
            );
            this.errorBoxText = alertTitles.every(title => title === 'hasPastDueLetterSent');
            if (isAllPastDue) {
              this.headingText = this.copyService.getCopy(
                `billing.oneTimePayRefactor.alertMessages.heading-multiPastDue`
              );
              this.bodyText = this.copyService.getCopy(
                `billing.oneTimePayRefactor.alertMessages.body1-multiPastDue`,
                { dueDateMulti: format(earliestDate[0].dueDate, 'MM/DD/YYYY') }
              );
            } else {
              this.headingText = this.copyService.getCopy(
                `billing.oneTimePayRefactor.alertMessages.heading-multipleAlerts`
              );
              this.bodyText = this.copyService.getCopy(
                `billing.oneTimePayRefactor.alertMessages.body1-multipleAlerts`
              );
            }
          }
        }
      });
  }

  alertLinkEvent(text: string) {
    this.modalService.open('oneTimePayCancelModal');
    this.linkEventText = text;
  }

  linkTextEvent(linkEventObj: { text: string; billAccountNumber: string }) {
    this.modalService.open('oneTimePayCancelModal');
    this.linkEventText = linkEventObj.text;
    this.alertLinkEventBillAccountNumber = linkEventObj.billAccountNumber;
  }

  cancel() {
    this.modalService.open('oneTimePayCancelModal');
  }

  stay() {
    this.modalService.close(this.cancelModalConfig.modalId);
    this.modalService.close('deleteAccountModal');
    // reset linkEventText
    this.linkEventText = undefined;
  }

  leave() {
    this.modalService.closeAll();
    this.store.dispatch(
      OneTimePayActions.leaveOTPFlow({
        billAccountNumber: this.alertLinkEventBillAccountNumber,
        id: this.linkEventText
      })
    );
  }

  removeAccount() {
    this.store.dispatch(
      OneTimePayActions.removeOneTimePayBillAccount({ billAccountNumber: this.selectedAccountId })
    );
    // remove account selection from state if account is removed
    this.store.dispatch(
      pendingPaymentActions.RemovePendingPaymentAction({
        billAccountNumber: this.selectedAccountId
      })
    );
    this.oneTimePayForm.removeControl(this.selectedAccountId);
    this.modalService.close('deleteAccountModal');
    this.accountListOptions$ = this.oneTimePayFacade.getBillAccountsWithAlertMessageList();
    this.oneTimePayFacade.sendAnalytics({ buttonAnalyticName: 'RemoveAccount:Remove' });
    this.getAlertMessage();
    this.getForm();
    this.calenderOptions();
  }

  preprocessPaymentPayload(otpForm: UntypedFormGroup, dueDate: string): OTPReviewData[] {
    let selectedPaymentAccount;
    let selectedPaymentDateFormatted;
    const billAccountDict: Dictionary<string> = {};
    const pageData: OTPReviewData[] = [];

    for (const control in otpForm.controls) {
      if (control === 'paymentMethod') {
        selectedPaymentAccount = otpForm.controls[control].value.text;
      } else if (control === 'paymentDate') {
        if (!selectedPaymentDateFormatted) {
          if (otpForm.controls[control].value.id === 'TODAY') {
            selectedPaymentDateFormatted = dateFormat(new Date(), 'YYYY-MM-DD');
          } else if (otpForm.controls[control].value.id === 'DUEDATE') {
            selectedPaymentDateFormatted = dateFormat(dueDate, 'YYYY-MM-DD');
          }
        }
      } else if (control === 'paymentDatePicker') {
        if (!selectedPaymentDateFormatted) {
          selectedPaymentDateFormatted = dateFormat(
            otpForm.controls['paymentDatePicker'].value,
            'YYYY-MM-DD'
          );
        }
      } else {
        billAccountDict[control] = otpForm.controls[control].value;
      }
    }

    // eslint-disable-next-line guard-for-in
    for (const billAccountNumber in billAccountDict) {
      pageData.push({
        billAccountNumber: billAccountNumber,
        selectedPaymentAccount: selectedPaymentAccount,
        selectedPaymentDate: selectedPaymentDateFormatted,
        selectedPaymentAmount: Number(billAccountDict[billAccountNumber])
      });
    }

    return pageData;
  }

  getPaymentError(): Observable<string> {
    return this.oneTimePayFacade.getPaymentErrorCode().pipe(
      map(errorCode => {
        if (PaymentMethodRelatedPaymentErrorCodes.includes(errorCode)) {
          this.spinner.stop();
          this.isDisabledAfterClick = false;
          this.oneTimePayFacade.sendAnalytics({
            pageAnalyticName: 'Error',
            pageAnalyticSubCategory3: errorCode
          });
          return this.copyService.getCopy(
            `billing.paymentFailureVagueErrorMessages.body.${errorCode}`
          );
        }
      })
    );
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }

  private openPaymentMethodModal(modalConfig: PaymentMethodAddEditConfig) {
    this.paymentMethodAddEditConfig$.next(modalConfig);
    this.modalService.open('paymentMethodModal', true);
  }

  private openChoosePaymentAmountModal(billAccountNumber: string) {
    this.choosePaymentAmountConfig$.next(billAccountNumber);
    this.modalService.open('choosePaymentAmountModal', true);
  }

  /**
   * @author: Abhishek Singh
   * @param form : Takes a form group and adds the new controls to the form
   * @returns FormGroup : Return the form which has the new controls and the initialized values.
   */
  private buildControls(
    form: UntypedFormGroup,
    options: OneTimePaySetup[],
    otpEntities: Dictionary<OneTimePaySetup>
  ): UntypedFormGroup {
    for (const option of options) {
      form.addControl(
        option.billAccountNumber,
        new UntypedFormControl(
          <number>otpEntities[option.billAccountNumber].selectedPaymentAmount || option.minDue
        )
      );
    }
    return form;
  }
}
